import { termsPath } from "../../constants.ts";
import { usePageMeta } from "./pageMeta.ts";
import { Support } from "./support.tsx";

const sectionStyle =
  "mb-6 text-text-secondary-light dark:text-text-secondary leading-relaxed text-sm";
const headingStyle =
  "text-lg font-semibold mt-8 mb-3 text-text-primary-light dark:text-text-primary";

export const RefundPolicy = () => {
  usePageMeta({
    title: "Refund Policy | prompt2bot",
    description: "How refunds work on prompt2bot.",
  });
  return (
    <div className="max-w-2xl mx-auto my-10 p-6 rounded-lg shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary">
      <h1 className="text-2xl font-bold mb-2 text-center">Refund Policy</h1>
      <p className="text-center text-xs text-text-tertiary-light dark:text-text-tertiary mb-8">
        Last updated: February 15, 2026
      </p>

      <p className={sectionStyle}>
        We want you to be fully satisfied with prompt2bot. If you are not happy
        with your purchase, we offer refunds under the following conditions.
      </p>

      <h2 className={headingStyle}>Eligibility</h2>
      <div className={sectionStyle}>
        <ul className="list-disc list-inside space-y-2">
          <li>
            Refunds are available within <strong>14 days</strong>{" "}
            of the initial purchase date.
          </li>
          <li>
            The refund window applies to the first payment on a new
            subscription. Subsequent recurring charges are eligible for refund
            only if requested within 14 days of that specific charge.
          </li>
          <li>
            Service must not have been used excessively during the refund period
            (i.e., beyond what a reasonable evaluation would entail).
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>How to Request a Refund</h2>
      <div className={sectionStyle}>
        <ol className="list-decimal list-inside space-y-2">
          <li>
            Contact us via Discord or WhatsApp (see below) within 14 days of
            your charge.
          </li>
          <li>
            Include your account email, the approximate date of the charge, and
            the reason for the refund request.
          </li>
          <li>
            We will review your request and respond within 3 business days.
          </li>
        </ol>
      </div>

      <h2 className={headingStyle}>Refund Processing</h2>
      <div className={sectionStyle}>
        <ul className="list-disc list-inside space-y-2">
          <li>
            Approved refunds are processed to your original payment method.
          </li>
          <li>
            Please allow 5–10 business days for the refund to appear on your
            statement, depending on your bank or card issuer.
          </li>
          <li>
            Once a refund is processed, your subscription will be cancelled and
            your account will revert to the Free tier.
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>Exceptions</h2>
      <div className={sectionStyle}>
        Refunds may not be granted in the following cases:
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>The request is made more than 14 days after the charge.</li>
          <li>
            There is evidence of abuse, fraud, or violation of our{" "}
            <a
              href={termsPath}
              className="text-accent hover:underline font-medium"
            >
              Terms of Service
            </a>.
          </li>
          <li>Excessive usage of the Service during the refund period.</li>
        </ul>
      </div>

      <h2 className={headingStyle}>Cancellation vs. Refund</h2>
      <p className={sectionStyle}>
        Cancelling your subscription stops future charges but does not
        automatically trigger a refund for the current billing period. If you
        wish to receive a refund, you must submit a separate refund request as
        described above.
      </p>

      <h2 className={headingStyle}>Contact</h2>
      <p className={sectionStyle}>
        For refund requests or questions about this policy, please reach out:
      </p>
      <div className="text-sm">
        <Support />
      </div>
    </div>
  );
};
